import { forwardRef, useId } from 'react';

/**
 * Input controlado con label flotante y mensaje de error accesible.
 * El useId garantiza que aria-describedby vincule input y error correctamente.
 */
const Input = forwardRef(
  (
    {
      label,
      error,
      hint,
      leftIcon: LeftIcon,
      type = 'text',
      className = '',
      id: providedId,
      ...rest
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId || generatedId;
    const errorId = `${id}-error`;
    const hintId = `${id}-hint`;

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-slate-700">
            {label}
          </label>
        )}
        <div className="relative">
          {LeftIcon && (
            <LeftIcon
              className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
              aria-hidden="true"
            />
          )}
          <input
            ref={ref}
            id={id}
            type={type}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : hint ? hintId : undefined}
            className={[
              'block w-full rounded-xl border bg-white px-4 py-3 text-base',
              'placeholder:text-slate-400',
              'focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20',
              'disabled:bg-slate-100 disabled:text-slate-500',
              LeftIcon ? 'pl-10' : '',
              error ? 'border-status-red focus:border-status-red focus:ring-status-red/20' : 'border-slate-200',
              className,
            ].join(' ')}
            {...rest}
          />
        </div>
        {error && (
          <p id={errorId} role="alert" className="mt-1.5 text-sm text-status-red">
            {error}
          </p>
        )}
        {!error && hint && (
          <p id={hintId} className="mt-1.5 text-sm text-slate-500">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
export default Input;